import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { SubmitDealStepProps } from '@/types/submit-deal.types';

export const StepLicenses = ({ formData, updateFormData, errors, clearError, t }: SubmitDealStepProps) => {
  const handleChange = (field: string, value: string | boolean) => {
    updateFormData('licensing', { [field]: value });
    clearError(field);
  };

  // AJOUTÉ — section technologie, distincte de licensing dans le schéma
  // (formData.technology, voir submit-deal.types.ts) mais regroupée avec
  // les licences dans ce même step à l'écran, puisque les deux répondent
  // à la même question de fond pour l'acheteur.
  const handleTechChange = (field: string, value: string | boolean) => {
    updateFormData('technology', { [field]: value });
    clearError(field);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Label>{t.step3.specialLicensesRequired} *</Label>
        <RadioGroup
          value={formData.licensing.specialLicensesRequired ? 'yes' : 'no'}
          onValueChange={(value) => handleChange('specialLicensesRequired', value === 'yes')}
          className="flex space-x-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="licensesYes" />
            <Label htmlFor="licensesYes" className="font-normal">{t.step3.yes}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="licensesNo" />
            <Label htmlFor="licensesNo" className="font-normal">{t.step3.no}</Label>
          </div>
        </RadioGroup>
      </div>

      {formData.licensing.specialLicensesRequired && (
        <div className="space-y-2">
          <Label htmlFor="licenseDetails">{t.step3.licenseDetails}</Label>
          <Textarea
            id="licenseDetails"
            value={formData.licensing.licenseDetails}
            onChange={(e) => handleChange('licenseDetails', e.target.value)}
            placeholder={t.step3.licenseDetailsPlaceholder}
            rows={3}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="regulatoryRequirements">{t.step3.regulatoryRequirements}</Label>
        <Textarea
          id="regulatoryRequirements"
          value={formData.licensing.regulatoryRequirements}
          onChange={(e) => handleChange('regulatoryRequirements', e.target.value)}
          placeholder={t.step3.regulatoryRequirementsPlaceholder}
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="legalDisputes">{t.step3.legalDisputes}</Label>
        <Textarea
          id="legalDisputes"
          value={formData.licensing.legalDisputes}
          onChange={(e) => handleChange('legalDisputes', e.target.value)}
          placeholder={t.step3.legalDisputesPlaceholder}
          rows={3}
        />
      </div>

      {/* AJOUTÉ — Technologie & Outils */}
      <div className="border-t pt-6 space-y-6">
        <h4 className="text-lg font-medium">{t.step3.technologySectionTitle}</h4>

        <div className="space-y-4">
          <Label>{t.step3.hasProprietaryTech}</Label>
          <RadioGroup
            value={formData.technology.hasProprietaryTech ? 'yes' : 'no'}
            onValueChange={(value) => handleTechChange('hasProprietaryTech', value === 'yes')}
            className="flex space-x-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="proprietaryTechYes" />
              <Label htmlFor="proprietaryTechYes" className="font-normal">{t.step3.yes}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="proprietaryTechNo" />
              <Label htmlFor="proprietaryTechNo" className="font-normal">{t.step3.no}</Label>
            </div>
          </RadioGroup>
        </div>

        {formData.technology.hasProprietaryTech && (
          <div className="space-y-2">
            <Label htmlFor="proprietaryTechDescription">{t.step3.proprietaryTechDescription}</Label>
            <Textarea
              id="proprietaryTechDescription"
              value={formData.technology.proprietaryTechDescription}
              onChange={(e) => handleTechChange('proprietaryTechDescription', e.target.value)}
              placeholder={t.step3.proprietaryTechDescriptionPlaceholder}
              rows={3}
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="techStackDescription">{t.step3.techStackDescription}</Label>
          <Textarea
            id="techStackDescription"
            value={formData.technology.techStackDescription}
            onChange={(e) => handleTechChange('techStackDescription', e.target.value)}
            placeholder={t.step3.techStackDescriptionPlaceholder}
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="thirdPartyTools">{t.step3.thirdPartyTools}</Label>
          <Textarea
            id="thirdPartyTools"
            value={formData.technology.thirdPartyTools}
            onChange={(e) => handleTechChange('thirdPartyTools', e.target.value)}
            placeholder={t.step3.thirdPartyToolsPlaceholder}
            rows={3}
          />
        </div>

        <div className="space-y-4">
          <Label>{t.step3.toolsTransferable}</Label>
          <RadioGroup
            value={formData.technology.toolsTransferable ? 'yes' : 'no'}
            onValueChange={(value) => handleTechChange('toolsTransferable', value === 'yes')}
            className="flex space-x-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="yes" id="toolsTransferableYes" />
              <Label htmlFor="toolsTransferableYes" className="font-normal">{t.step3.yes}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="no" id="toolsTransferableNo" />
              <Label htmlFor="toolsTransferableNo" className="font-normal">{t.step3.no}</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-2">
          <Label htmlFor="requiredBuyerTechSkills">{t.step3.requiredBuyerTechSkills}</Label>
          <Textarea
            id="requiredBuyerTechSkills"
            value={formData.technology.requiredBuyerTechSkills}
            onChange={(e) => handleTechChange('requiredBuyerTechSkills', e.target.value)}
            placeholder={t.step3.requiredBuyerTechSkillsPlaceholder}
            rows={2}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="hostingProvider">{t.step3.hostingProvider}</Label>
          <Input
            id="hostingProvider"
            value={formData.technology.hostingProvider}
            onChange={(e) => handleTechChange('hostingProvider', e.target.value)}
            placeholder={t.step3.hostingProviderPlaceholder}
          />
        </div>
      </div>
    </div>
  );
};